/**
 * Synthesized golf ball impact sound using Web Audio API.
 * No external files needed — generates a realistic "thwack" on the fly.
 *
 * The sound layers:
 * 1. Sharp noise burst — the "crack" of impact
 * 2. Low-frequency thump — the weight of the club head
 * 3. High-frequency ping — the metallic ring of the face
 */
export class AudioManager {
  private ctx: AudioContext | null = null;

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  /**
   * Play a synthesized golf ball impact sound.
   * Call this at the moment of launch.
   */
  playHitSound(): void {
    const ctx = this.ensureContext();
    this.playSingleHit(ctx, ctx.currentTime, 1.0);
  }

  /**
   * Play multiple staggered hit sounds for comparison/gear shot launches.
   * Each voice has slight pitch and timing variance for a natural "burst" effect.
   */
  playMultiHitSound(count = 3): void {
    const ctx = this.ensureContext();
    const now = ctx.currentTime;

    for (let i = 0; i < count; i++) {
      const offset = i * 0.07;                        // 70ms stagger
      const pitchScale = 0.9 + Math.random() * 0.2;   // 0.9–1.1 range
      this.playSingleHit(ctx, now + offset, pitchScale);
    }
  }

  /**
   * Internal: play one hit sound with pitch scaling and time offset.
   */
  private playSingleHit(ctx: AudioContext, startTime: number, pitchScale: number): void {
    // ── Layer 1: Noise burst (the "crack") ──
    const noiseLen = 0.08;
    const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * noiseLen, ctx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < noiseData.length; i++) {
      noiseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseData.length, 3);
    }

    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;

    const noiseBP = ctx.createBiquadFilter();
    noiseBP.type = 'bandpass';
    noiseBP.frequency.value = 3000 * pitchScale;
    noiseBP.Q.value = 0.8;

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.4, startTime);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.07);

    noiseSource.connect(noiseBP).connect(noiseGain).connect(ctx.destination);
    noiseSource.start(startTime);
    noiseSource.stop(startTime + noiseLen);

    // ── Layer 2: Low thump (club head weight) ──
    const thump = ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(120 * pitchScale, startTime);
    thump.frequency.exponentialRampToValueAtTime(60 * pitchScale, startTime + 0.05);

    const thumpGain = ctx.createGain();
    thumpGain.gain.setValueAtTime(0.25, startTime);
    thumpGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.06);

    thump.connect(thumpGain).connect(ctx.destination);
    thump.start(startTime);
    thump.stop(startTime + 0.08);

    // ── Layer 3: Metallic ping (face ring) ──
    const ping = ctx.createOscillator();
    ping.type = 'sine';
    ping.frequency.setValueAtTime(4200 * pitchScale, startTime);
    ping.frequency.exponentialRampToValueAtTime(3800 * pitchScale, startTime + 0.12);

    const pingGain = ctx.createGain();
    pingGain.gain.setValueAtTime(0.06, startTime);
    pingGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.15);

    ping.connect(pingGain).connect(ctx.destination);
    ping.start(startTime);
    ping.stop(startTime + 0.18);
  }
}
